/**
 * i18n Content Helper
 * 
 * Utilities to handle multilingual content from the database
 * based on the current user language (react-i18next)
 */

export type Language = 'en' | 'fr' | 'ar';

export interface MultilingualField {
  en: string | null;
  fr?: string | null;
  ar?: string | null;
}

/**
 * Get the value of a multilingual field based on current language
 * Falls back to English if the requested language is not available
 * 
 * @param field - Object with en, fr, ar properties
 * @param lang - Current language code
 * @returns The content in the requested language or fallback
 */
export function getLocalizedContent(
  field: MultilingualField | null | undefined,
  lang: Language = 'en'
): string {
  if (!field) return '';
  
  // Try requested language
  const content = field[lang];
  if (content) return content;
  
  // Fallback to English
  if (field.en) return field.en;
  
  // Last resort: return first available
  return field.fr || field.ar || '';
}

/**
 * Extract multilingual fields from a database object
 * 
 * Example:
 * extractMultilingualFields(slide, 'title') 
 * → { en: slide.title_en, fr: slide.title_fr, ar: slide.title_ar }
 */
export function extractMultilingualFields<T extends Record<string, any>>(
  obj: T,
  fieldName: string
): MultilingualField {
  return {
    en: obj[`${fieldName}_en`] || obj[fieldName] || null,
    fr: obj[`${fieldName}_fr`] || null,
    ar: obj[`${fieldName}_ar`] || null,
  };
}

/**
 * Get localized content directly from a database object
 * 
 * Example:
 * getLocalizedField(slide, 'title', 'fr')
 * → slide.title_fr || slide.title_en || slide.title
 */
export function getLocalizedField<T extends Record<string, any>>(
  obj: T,
  fieldName: string,
  lang: Language = 'en'
): string {
  const fields = extractMultilingualFields(obj, fieldName);
  return getLocalizedContent(fields, lang);
}

/**
 * Transform a database object to use localized fields
 * Useful for backward compatibility with components expecting single-language fields
 * 
 * Example:
 * localizeObject(slide, 'fr', ['title', 'content'])
 * → { ...slide, title: slide.title_fr, content: slide.content_fr }
 */
export function localizeObject<T extends Record<string, any>>(
  obj: T,
  lang: Language,
  fields: string[]
): T {
  const localized = { ...obj };
  
  for (const field of fields) {
    localized[field] = getLocalizedField(obj, field, lang);
  }
  
  return localized;
}

/**
 * Hook to get current language from react-i18next
 * Safe to use in components
 */
export function useCurrentLanguage(): Language {
  // This will be imported from react-i18next in the component
  // For now, return 'en' as default
  return 'en';
}
